/**
 * @file Application Global State Store.
 *
 * @desc This is a vanilla Zustand store, allowing us to keep an application
 * global state.
 *
 * It's specially useful for state that needs to be accessed by the Routers,
 * since it's hard to access Provider/Hooks inside `beforeLoad`/`loader`
 * functions (thus why a vanilla store).
 *
 * *IMPORTANT*: As a general rule, the global state should be kept to a
 * minimum and it should NOT be fetched here!
 *
 * We should use TanStack Query to synchronize with the backend. Please read
 * for more information:
 *  https://tanstack.com/query/latest/docs/framework/react/guides/does-this-replace-client-state
 */

import { persist } from "zustand/middleware"
import { createStore } from "zustand/vanilla"

import type { Permission } from "@/lib/account/permission"
import { checkPermissions } from "@/lib/account/utils"

import type { UserRole } from "@boundless/bl-api-client/allauth/models"

interface GlobalState {
  // The company selected by the user that should be displayed and acted upon
  activeCompanyUuid: string | null
  // Whether the user is authenticated or not.
  isAuthenticated: boolean
  // Authenticated User Role
  userRole: UserRole | null
  // Permission System
  permissions: Permission[]
  hasPermissions: (perm: ReadonlyArray<Permission>) => boolean
  // Partner Logo
  partnerLogo: string | null
  // Embed context partner id
  embedPartnerId: string | null
  // Embed context user id
  embedUserId: string | null
  // Embed: partner-collected values used to prefill the create-account form.
  // Memory-only (not persisted); cleared on embed unmount.
  embedPrefillEmail: string | null
  embedPrefillFirstName: string | null
  embedPrefillLastName: string | null
  // Perks Enabled
  perksEnabled: boolean
  // Embed: hide the UI header when loaded via embed
  embedHideHeader: boolean
  // Primary Color
  primaryColor: string | null
  // Partner theme header gradient color override
  headerGradientColor: string | null
  // Partner theme accent color, used for the proof panel highlights
  accentColor: string | null
  // Reset to the initial authentication state.
  resetAuth: () => void
  // Requested Documents Card was closed by the user.
  requestDocumentsCardClosed: boolean
}

const initialAuthState = {
  isAuthenticated: false,
  userRole: null,
  permissions: [],
}

export const store = createStore<GlobalState>()(
  persist(
    (set, get) => ({
      ...initialAuthState,
      activeCompanyUuid: null,
      partnerLogo: null,
      embedPartnerId: null,
      embedUserId: null,
      embedPrefillEmail: null,
      embedPrefillFirstName: null,
      embedPrefillLastName: null,
      primaryColor: null,
      perksEnabled: true,
      embedHideHeader: false,
      headerGradientColor: null,
      accentColor: null,
      permissions: [],
      hasPermissions: (perms: ReadonlyArray<Permission>) => {
        return checkPermissions(get().permissions, perms)
      },
      resetAuth: () => {
        set({ ...initialAuthState })
      },
      requestDocumentsCardClosed: false,
    }),
    {
      name: "global",
      // Only persist these fields
      partialize: (state) => ({
        activeCompanyUuid: state.activeCompanyUuid,
        requestDocumentsCardClosed: state.requestDocumentsCardClosed,
      }),
    },
  ),
)
